import { useI18nSubscription } from '../../i18n/useI18nSubscription.js';
import { i18n } from '../../../i18n/index.js';
// Панель задач внутри карточки сущности (сделка / контакт / компания).
// Тот же грид, что и на странице «Задачи», но отфильтрованный по сущности:
// колонка самой сущности скрыта, а созданная задача сразу к ней привязывается.
import { useState, type ReactElement } from 'react';
import { Button, Spin } from 'antd';
import { EmptyState } from '../../../components/entity-detail/EmptyState.js';

import type { TaskItem, TaskStatus } from '../api/tasks.client.js';
import { useTaskGridProfile } from '../hooks/useTaskGridProfile.js';
import { useTasks } from '../hooks/useTasks.js';
import { useDeleteTaskMutation, useUpdateTaskMutation } from '../hooks/useTaskMutations.js';
import { TaskFormModal } from './TaskFormModal.js';
import { TasksGrid } from './TasksGrid.js';
import { ErrorState } from '../../../shared/ui/ErrorState.js';

interface EntityTasksPanelProps {
  dealId?: string;
  contactId?: string;
  companyId?: string;
  /** Задачи по документу: «подготовить приложение», «согласовать редакцию». */
  documentId?: string;
  title?: string;
}

export function EntityTasksPanel({
  dealId,
  contactId,
  companyId,
  documentId,
  title = i18n.t('crm.features.features.tasks.components.entitytaskspanel.задачи'),
}: EntityTasksPanelProps): ReactElement {
  useI18nSubscription();
  const filters = {
    ...(dealId ? { dealId } : {}),
    ...(contactId ? { contactId } : {}),
    ...(companyId ? { companyId } : {}),
    ...(documentId ? { documentId } : {}),
  };
  const { data, isLoading, isError, error } = useTasks(filters);
  const grid = useTaskGridProfile();
  const updateMut = useUpdateTaskMutation();
  const deleteMut = useDeleteTaskMutation();

  const [formOpen, setFormOpen] = useState(false);
  const [editingTask, setEditingTask] = useState<TaskItem | null>(null);

  const rows = data ?? [];
  const suppressColumns = [
    ...(dealId ? (['deal'] as const) : []),
    ...(contactId ? (['contact'] as const) : []),
    ...(companyId ? (['company'] as const) : []),
  ];

  const openCreate = (): void => {
    setEditingTask(null);
    setFormOpen(true);
  };

  return (
    <div className="px-6 py-5">
      <div className="mb-4 flex items-center justify-between">
        <div className="text-[11px] font-semibold uppercase tracking-[0.07em] text-[var(--ant-color-text-secondary)]">
          {title}
        </div>
        <Button type="primary" size="small" onClick={openCreate}>
          {i18n.t('crm.features.features.tasks.components.entitytaskspanel.задача')}
        </Button>
      </div>

      {isError ? (
        <ErrorState
          title={i18n.t(
            'crm.features.features.tasks.components.entitytaskspanel.не_удалось_загрузить_задачи',
          )}
          error={error}
        />
      ) : isLoading ? (
        <div className="flex justify-center py-6">
          <Spin />
        </div>
      ) : rows.length === 0 ? (
        <EmptyState
          text={i18n.t(
            'crm.features.features.tasks.components.entitytaskspanel.здесь_пока_нет_задач',
          )}
        />
      ) : (
        <TasksGrid
          rows={rows}
          compact
          profile={grid}
          suppressColumns={[...suppressColumns]}
          onEdit={(task) => {
            setEditingTask(task);
            setFormOpen(true);
          }}
          onDelete={(task) => deleteMut.mutate(task.id)}
          onQuickStatus={(task: TaskItem, status: TaskStatus) =>
            updateMut.mutate({ id: task.id, input: { status } })
          }
          quickStatusPendingId={updateMut.isPending ? (updateMut.variables?.id ?? null) : null}
          deletePendingId={deleteMut.isPending ? (deleteMut.variables ?? null) : null}
        />
      )}

      {formOpen ? (
        <TaskFormModal
          open
          onClose={() => setFormOpen(false)}
          task={editingTask}
          {...(dealId ? { lockedDealId: dealId } : {})}
          {...(contactId ? { lockedContactId: contactId } : {})}
          {...(companyId ? { lockedCompanyId: companyId } : {})}
          {...(documentId ? { lockedDocumentId: documentId } : {})}
        />
      ) : null}
    </div>
  );
}
